import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

const STATUS = {
  unassigned: { label: 'Unassigned', cls: 'bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-300' },
  scheduled: { label: 'Assigned', cls: 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400' },
  in_progress: { label: 'In progress', cls: 'bg-sky-50 text-sky-600 dark:bg-sky-900/20 dark:text-sky-300' },
  completed: { label: 'Done', cls: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/10 dark:text-emerald-400' },
  missed: { label: 'Missed', cls: 'bg-rose-50 text-rose-600 dark:bg-rose-900/20 dark:text-rose-300' },
};
const initials = (n) => (n || '?').split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();

const VisitRow = ({ visit, onOpen }) => {
  const st = STATUS[visit.status] || STATUS.scheduled;
  const unassigned = visit.status === 'unassigned';
  return (
    <div className="grid grid-cols-1 items-center gap-3 border-t border-gray-50 dark:border-gray-800 px-4 py-3 md:grid-cols-[1.4fr_1.4fr_1.1fr_1.4fr_auto_auto]">
      {/* Client */}
      <div className="flex items-center gap-3">
        <span className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg bg-blue-50 dark:bg-blue-900/20 text-[11px] font-bold text-blue-700 dark:text-blue-400">{initials(visit.client.name)}</span>
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-gray-800 dark:text-gray-200">{visit.client.name}</div>
          <div className="truncate text-[10px] text-gray-400 dark:text-gray-500">{[visit.client.code, visit.client.postcode].filter(Boolean).join(' · ')}</div>
        </div>
      </div>
      {/* Visit */}
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold text-gray-700 dark:text-gray-200">{visit.package_name}</div>
        <div className="truncate text-[11px] text-gray-400 dark:text-gray-500">{[visit.visit_type, `${visit.start}${visit.end ? `–${visit.end}` : ''}`].filter(Boolean).join(' · ')}</div>
      </div>
      {/* Tasks */}
      <div className="min-w-0 text-[10px] text-gray-400 dark:text-gray-500">
        <div className="font-semibold text-gray-500 dark:text-gray-400">{visit.task_count} care task{visit.task_count === 1 ? '' : 's'}</div>
        <div className="truncate">{visit.task_preview.join(' · ')}</div>
      </div>
      {/* Assignment */}
      <div className="flex min-w-0 items-center gap-2">
        <span className={`grid h-8 w-8 flex-shrink-0 place-items-center rounded-full text-[10px] font-bold ${unassigned ? 'bg-gray-100 dark:bg-[#31373f] text-gray-400 dark:text-gray-500' : 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'}`}>
          {unassigned ? '?' : initials(visit.carers[0]?.name)}
        </span>
        <div className="min-w-0">
          {unassigned ? (
            <>
              <div className="text-sm font-semibold text-purple-500 dark:text-purple-300">Unassigned</div>
              <div className="text-[10px] text-gray-400 dark:text-gray-500">Choose a zone-matched carer</div>
            </>
          ) : (
            <>
              <div className="truncate text-sm font-semibold text-gray-700 dark:text-gray-200">{visit.carers.map((c) => c.name).join(', ')}</div>
              <div className="truncate text-[10px] text-gray-400 dark:text-gray-500">Preferred: {visit.preferred_carer}</div>
            </>
          )}
        </div>
      </div>
      {/* Status */}
      <span className={`inline-flex justify-center rounded-md px-2.5 py-1 text-[9px] font-bold uppercase ${st.cls}`}>{st.label}</span>
      {/* Action */}
      <button
        onClick={() => onOpen(visit)}
        className={`h-8 rounded-lg border px-3 text-[11px] font-bold ${unassigned ? 'border-emerald-200 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 dark:border-emerald-900/50 dark:bg-emerald-900/10 dark:text-emerald-400' : 'border-blue-200 text-blue-600 hover:bg-blue-50 dark:border-blue-900/50 dark:text-blue-400 dark:hover:bg-blue-900/20'}`}
      >
        {unassigned ? 'Assign' : 'Manage'}
      </button>
    </div>
  );
};

const ZoneCard = ({ zone, onOpen, onEditZone }) => {
  const [collapsed, setCollapsed] = useState(false);
  return (
    <section className="overflow-hidden rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-[#262b33] shadow-sm">
      <header className="flex items-center justify-between gap-4 border-b border-gray-100 dark:border-gray-800 px-4 py-3">
        <div className="flex items-center gap-3">
          <button onClick={() => setCollapsed((c) => !c)} className="grid h-7 w-7 place-items-center rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#31373f]">
            <ChevronDown className={`h-4 w-4 transition ${collapsed ? '-rotate-90' : ''}`} />
          </button>
          <span className="h-3 w-3 rounded-full" style={{ background: zone.color || '#2563eb' }} />
          <div>
            <div className="text-sm font-bold uppercase tracking-wide text-gray-700 dark:text-gray-200">{zone.name}</div>
            <div className="text-[11px] text-gray-400 dark:text-gray-500">{zone.postcode_prefixes?.length ? `Travel area · ${zone.postcode_prefixes.join(', ')}` : 'No travel area'}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 text-[11px] font-semibold">
          <span className="rounded-md bg-gray-100 dark:bg-[#31373f] px-2 py-1 text-gray-500 dark:text-gray-300">{zone.visit_count} visits</span>
          {zone.unassigned_count > 0 && <span className="rounded-md bg-purple-50 dark:bg-purple-900/20 px-2 py-1 text-purple-600 dark:text-purple-300">{zone.unassigned_count} unassigned</span>}
          {zone.id != null && onEditZone && (
            <button onClick={() => onEditZone(zone)} className="rounded-md border border-blue-200 dark:border-blue-900/50 px-2.5 py-1 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20">Edit Zone</button>
          )}
        </div>
      </header>
      {!collapsed && (
        <div>
          {zone.visits.length === 0
            ? <div className="px-4 py-6 text-center text-sm text-gray-400 dark:text-gray-500">No visits in this zone.</div>
            : zone.visits.map((v) => <VisitRow key={v.assignment_id} visit={v} onOpen={onOpen} />)}
        </div>
      )}
    </section>
  );
};

const PackageClientView = ({ zones = [], onOpen, onEditZone }) => {
  if (zones.length === 0) {
    return <div className="rounded-2xl border border-dashed border-gray-200 dark:border-gray-800 bg-white dark:bg-[#262b33] px-4 py-10 text-center text-sm text-gray-400 dark:text-gray-500">No care packages for this day.</div>;
  }
  return (
    <div className="flex flex-col gap-4">
      {zones.map((z) => <ZoneCard key={z.id ?? 'unzoned'} zone={z} onOpen={onOpen} onEditZone={onEditZone} />)}
    </div>
  );
};

export default PackageClientView;
